import React, { useState } from 'react'
import { Moon, Sun, Search, ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'
import { useNotificationsContext } from '@/context/NotificationsContext'
import { NotificationBell } from '@/features/hubspot/components/NotificationBell'
import { NotificationTray } from '@/features/hubspot/components/NotificationTray'
import type { NotificationRecord } from '@/features/hubspot/services/notificationsApi'

interface HeaderProps {
  title?: string
  icon?: React.ReactNode
  // When set, renders a back arrow before the title that navigates to this
  // path — for a sub-route of a page that has its own list/landing view (e.g.
  // a campaign's builder/detail screen going back to the campaigns list).
  backTo?: string
  search?: boolean
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  // Lets the search field fill the bar instead of capping at max-w-md. Opt-in so
  // the pages that want a compact search keep it.
  searchGrow?: boolean
  // Rendered below the search box while it's focused (e.g. a results dropdown).
  // Owned by the caller so Header stays presentation-only.
  searchDropdown?: React.ReactNode
  // Page-specific controls (e.g. a manual refresh button) rendered before the
  // global notification/theme controls — generic slot so pages other than
  // dashboard can use it too, rather than hardcoding one page's button here.
  actions?: React.ReactNode
}

export const Header: React.FC<HeaderProps> = ({
  title,
  icon,
  backTo,
  search = false,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  searchGrow = false,
  searchDropdown,
  actions,
}) => {
  const { isDarkMode, setIsDarkMode, currentTheme } = useTheme()
  const { notifications, dismiss } = useNotificationsContext()
  const navigate = useNavigate()
  const [searchFocused, setSearchFocused] = useState(false)
  // Selecting a dropdown result uses onMouseDown+preventDefault (see
  // SearchResultsDropdown) so the input never actually blurs during a click —
  // this delay is only a safety net for keyboard/other blur paths, giving any
  // in-flight interaction a moment to complete before the dropdown unmounts.
  const blurTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleSearchFocus = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
    setSearchFocused(true)
  }

  const handleSearchBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setSearchFocused(false), 150)
  }

  // Navigate to chat page carrying lead context as router state
  const handleStartAction = (notification: NotificationRecord) => {
    navigate('/dashboard/chat', {
      state: { leadNotification: notification },
    })
  }

  const handleLeadAction = (notification: NotificationRecord, promptText: string) => {
    navigate('/dashboard/chat', {
      state: { leadNotification: notification, leadPromptText: promptText },
    })
  }

  return (
    <div
      className="pl-4 pr-4 md:pr-8 h-[60px] shrink-0 relative"
      style={{ backgroundColor: currentTheme.secondary, borderBottom: `1px solid ${currentTheme.border}` }}
    >
      {/* Portals to document.body — Header is the shared chrome for every page
          that isn't Chat (Dashboard, Settings, ...); ChatPage has its own copy
          of this Tray since it uses ChatTopBar instead of Header. Without both,
          a notification firing while you're on Dashboard/Settings never shows. */}
      <NotificationTray
        notifications={notifications}
        onDismiss={dismiss}
        onStartAction={handleStartAction}
      />
      <div className="flex justify-between items-center h-full">
        {search ? (
          <div className={`relative ${searchGrow ? 'mr-4' : ''}`}>
            <div
              className={`flex items-center gap-2.5 h-11 rounded-xl px-4 transition-[width] duration-300 ease-out ${searchFocused ? 'w-[28rem]' : 'w-96'}`}
              style={{
                backgroundColor: currentTheme.secondary,
                border: `1px solid ${currentTheme.borderStrong}`,
              }}
            >
              <Search size={18} style={{ color: currentTheme.mutedText }} className="shrink-0" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder={searchPlaceholder}
                className="flex-1 min-w-0 bg-transparent outline-none text-sm"
                style={{ color: currentTheme.text }}
              />
            </div>
            {searchFocused && searchDropdown}
          </div>
        ) : (
          <div className="flex items-center gap-2.5 min-w-0">
            {backTo && (
              <button
                type="button"
                onClick={() => navigate(backTo)}
                aria-label="Back"
                className="flex items-center justify-center p-1.5 rounded-lg shrink-0 hover:opacity-70"
                style={{ color: currentTheme.text }}
              >
                <ArrowLeft size={18} />
              </button>
            )}
            {title && (
              <>
                {icon && (
                  <span
                    className="flex items-center justify-center shrink-0"
                    style={{ color: currentTheme.accent }}
                  >
                    {icon}
                  </span>
                )}
                <h1
                  className="text-base font-bold leading-none truncate"
                  style={{ color: currentTheme.text }}
                >
                  {title}
                </h1>
              </>
            )}
          </div>
        )}

        <div className="flex items-center gap-3 md:gap-6 shrink-0">
          {actions}

          <NotificationBell
            notifications={notifications}
            onDismiss={dismiss}
            onStartAction={handleStartAction}
            onLeadAction={handleLeadAction}
          />

          <button
            type="button"
            onClick={() => setIsDarkMode(!isDarkMode)}
            aria-label="Toggle theme"
            className="p-2 rounded-lg transition-all duration-300 hover:opacity-80"
            style={{
              backgroundColor: currentTheme.secondary,
              color: currentTheme.text,
              border: `1px solid ${currentTheme.border}`,
            }}
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Header
